import {useFormik} from 'formik'
import * as Yup from 'yup'
import {IAllUserNode, IResEditMember} from '../../../types/users'
import {EDIT_USER, EDIT_USER_STR} from '../../../gql/mutations/userMutations'
import {useMutation, useQuery} from '@apollo/client'
import {useEffect, useState, useRef} from 'react'
import {toast} from 'react-toastify'
import {Col, Form, Row, Modal, Button} from 'react-bootstrap'
import {toAbsoluteUrl} from '../../../../../_metronic/helpers'
import {parseId} from '../../../helpers'
import {IResRolesGroups} from '../../../types/roles'
import {GET_ROLES_GROUP} from '../../../gql/queries/rolesGroupQuery'
import {GET_ALL_ORGANIZATIONS} from '../../../gql/queries/organizationsQuery'
import {prependBackendUrl} from '../../../gql/client'
import {useUploadFile} from '../../../hooks/useUploadFile'
import {useGetUser} from '../../../store/userStore'
import {FormattedMessage, useIntl} from 'react-intl'
import {ONE_USER_MESSAGES as M} from '../oneUserMessages'

// Descriptores que se reusan de otras carpetas ya migradas. Se declaran como
// consts para no repetir el objeto en cada uso.
const ERR_WITH_MESSAGE = {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}
const SOMETHING_HAPPENED = {
  id: 'common.error.somethingHappened',
  defaultMessage: 'Something happened',
}
const CM_LOADING = {id: 'common.loading', defaultMessage: 'Loading...'}
const SELECT_ONE = {id: 'admin.users.select.one', defaultMessage: 'Select One'}
const ADMIN_NO_OWN_ROLE = {
  id: 'admin.users.hint.noOwnRolePermission',
  defaultMessage: "You don't have permissions to change your own role",
}
const ADMIN_ONLY_SUPERADMIN_ORG = {
  id: 'admin.users.hint.onlySuperAdminOrganization',
  defaultMessage: 'Only SuperAdministrador can change the organization',
}

interface IPropfileProps {
  node: IAllUserNode
  refetch: () => void
}

const INIT_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  cellphone: '',
  applyDeductions: '',
  rol: '',
  npn: '',
  organizationId: '',
  avatar: null as File | null,

}
const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email format').required('Email is required'),
  cellphone: Yup.string(),
  applyDeductions: Yup.string(),
})

export const ProfileDetailsSetting: React.FC<IPropfileProps> = ({node, refetch}) => {
  const intl = useIntl()
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [showCameraModal, setShowCameraModal] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [cameraActive, setCameraActive] = useState(false)
  const normalizeId = (id?: string | number | null) => (id ? `${parseId(id)}` : '')

  // Get current user's role
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUser = useGetUser((s) => s.user?.user)
  const currentUserId = currentUser?.id
  // Check user roles
  const isAgente = userGroups.includes('Agente')
  const isAsesor = userGroups.includes('Asesor')
  const isSupervisor = userGroups.includes('Supervisor')
  const isSuperAdministrador = userGroups.includes('SuperAdministrador')
  const isEditingSelf = !!(node && currentUserId && node.id === currentUserId)

  // Determine if role selector should be disabled
  const isRoleSelectorDisabled = !!(isAgente || isAsesor) && isEditingSelf

  // Determine if organization selector should be disabled
  const isOrganizationSelectorDisabled = !isSuperAdministrador

  const {
    data: dataRoles,
    loading: loadingRoles,
    error: errorRoles,
  } = useQuery<IResRolesGroups>(GET_ROLES_GROUP, { fetchPolicy: 'no-cache' })

  // Query for organizations
  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: { first: 100 },
    fetchPolicy: 'no-cache',
  })

  // Para actualizar sin archivo (mutation normal de Apollo)
  const [gqlEdit, { data, loading, error }] = useMutation<IResEditMember>(EDIT_USER)

  // Para actualizar con archivo (fetch directo con FormData)
  const [uploadFile, {data: dataUpload, loading: loadingUpload}] =
    useUploadFile<IResEditMember>()

  const formik = useFormik({
    initialValues: INIT_VALUES,
    validationSchema,

    onSubmit: async (v, o) => {
      const applyDeductionsValue = v.applyDeductions
        ? v.applyDeductions === 'yes'
        : node.applyDeductions

      const userData = {
        firstName: v.firstName,
        lastName: v.lastName,
        email: v.email,
        cellphone: v.cellphone,
        npn: v.npn,
        applyDeductions: applyDeductionsValue,
        isActive: true,
        primaryGroup: v.rol ? parseId(v.rol) : undefined,
        organizationId: v.organizationId ? parseId(v.organizationId) : undefined,

      }

      // Si hay un archivo, usar FormData (como en ModalAddDocsPolicy)
      if (v.avatar) {
        const formdata = new FormData()
        formdata.append('0', v.avatar, v.avatar.name)

        const map = { '0': ['variables.userData.avatar'] }

        const obj = {
          query: EDIT_USER_STR,
          variables: {
            userData: {
              ...userData,
              avatar: null, // Se reemplazará con el archivo del FormData
            },
            userId: parseId(node.id),
          },
        }

        formdata.append('map', JSON.stringify(map))
        formdata.append('operations', JSON.stringify(obj))

        uploadFile(formdata)
      } else {
        // Sin archivo, usar mutation normal de Apollo
        gqlEdit({
          variables: {
            userData,
            userId: parseId(node.id),
          },
        })
      }
    },
  })

  useEffect(() => {
    // Si no es SuperAdministrador y el usuario editado no tiene organización,
    // usar la organización del usuario actual logueado
    const organizationIdValue = node.organization
      ? normalizeId(node.organization.id)
      : (!isSuperAdministrador && currentUser?.organization?.id
        ? normalizeId(currentUser.organization.id)
        : '')

    formik.setValues({
      applyDeductions: node.applyDeductions ? 'yes' : 'no',
      cellphone: node.cellphone,
      email: node.email,
      firstName: node.firstName,
      lastName: node.lastName,
      rol: node.primaryGroup ? node.primaryGroup.id : '',
      npn: node.npn,
      organizationId: organizationIdValue,
      avatar: null, // No cargamos el avatar existente como File, solo lo mostramos en el preview

    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, isSuperAdministrador, currentUser])

  useEffect(() => {
    if (error) {
      toast.error(intl.formatMessage(ERR_WITH_MESSAGE, {message: error.message}))
    }
  }, [error])
  useEffect(() => {
    if (errorRoles) toast.error(intl.formatMessage(ERR_WITH_MESSAGE, {message: errorRoles.message}))
  }, [errorRoles])
  useEffect(() => {
    if (errorOrganizations) toast.error(intl.formatMessage(ERR_WITH_MESSAGE, {message: errorOrganizations.message}))
  }, [errorOrganizations])

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      })

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().catch(err => console.error('Play error:', err))
        }
        setCameraActive(true)
      }
    } catch (error) {
      console.error('Camera error:', error)
      toast.error(intl.formatMessage(M.toastCameraError))
    }
  }

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = (videoRef.current.srcObject as MediaStream).getTracks()
      tracks.forEach((track) => {
        track.stop()
      })
      videoRef.current.srcObject = null
    }
    setCameraActive(false)
  }

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d')
      if (context) {
        canvasRef.current.width = videoRef.current.videoWidth
        canvasRef.current.height = videoRef.current.videoHeight
        context.drawImage(videoRef.current, 0, 0)

        canvasRef.current.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], 'camera_capture.jpg', { type: 'image/jpeg' })
            formik.setFieldValue('avatar', file)
            const reader = new FileReader()
            reader.onloadend = () => {
              setAvatarPreview(reader.result as string)
            }
            reader.readAsDataURL(blob)
            stopCamera()
            setShowCameraModal(false)
            toast.success(intl.formatMessage(M.toastPhotoCaptured))
          }
        }, 'image/jpeg', 0.9)
      }
    }
  }

  useEffect(() => {
    return () => {
      if (cameraActive) {
        stopCamera()
      }
    }
  }, [])

  useEffect(() => {
    if (!data) return
    if (data?.updateUserMutation?.user?.id) {
      toast.success(intl.formatMessage(M.toastUserUpdated))
      refetch()
      formik.setFieldValue('avatar', null)
      setAvatarPreview(null)
    } else if (data?.updateUserMutation.errors?.length > 0) {
      toast.error(
        intl.formatMessage(ERR_WITH_MESSAGE, {
          message: data?.updateUserMutation.errors[0]?.message || intl.formatMessage(SOMETHING_HAPPENED),
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (!dataUpload) return
    if (dataUpload?.updateUserMutation?.user?.id) {
      toast.success(intl.formatMessage(M.toastUserAvatarUpdated))
      refetch()
      formik.setFieldValue('avatar', null)
      setAvatarPreview(null)
    } else if (dataUpload?.updateUserMutation?.errors?.length > 0) {
      toast.error(
        intl.formatMessage(ERR_WITH_MESSAGE, {
          message: dataUpload?.updateUserMutation?.errors[0]?.message || intl.formatMessage(SOMETHING_HAPPENED),
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpload])

  return (
    <div className='card mb-5 mb-xl-10'>
      <div
        className='card-header border-0 cursor-pointer'
        role='button'
        data-bs-toggle='collapse'
        data-bs-target='#kt_account_profile_details'
        aria-expanded='true'
        aria-controls='kt_account_profile_details'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>
            <FormattedMessage {...M.profileDetails} />
          </h3>
        </div>
      </div>

      <div id='kt_account_profile_details' className='collapse show'>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          <div className='card-body border-top p-9'>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <FormattedMessage {...M.avatar} />
              </label>
              <div className='col-lg-8'>
                <div className='image-input image-input-outline' data-kt-image-input='true'>
                  <div
                    className='image-input-wrapper w-125px h-125px'
                    style={{
                      backgroundImage: `url(${avatarPreview ||
                        (node.avatar ? prependBackendUrl(node.avatar) : toAbsoluteUrl('/media/avatars/blank.png'))
                        })`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  ></div>
                  <div className='d-flex gap-2'>
                    <label
                      className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                      data-bs-toggle='tooltip'
                      title={intl.formatMessage(M.changeAvatar)}
                    >
                      <i className='bi bi-pencil-fill fs-7'></i>
                      <input
                        type='file'
                        name='avatar'
                        accept='.png, .jpg, .jpeg, .webp'
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            formik.setFieldValue('avatar', file)
                            const reader = new FileReader()
                            reader.onloadend = () => {
                              setAvatarPreview(reader.result as string)
                            }
                            reader.readAsDataURL(file)
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <button
                      type='button'
                      className='btn btn-icon btn-circle btn-active-color-success w-25px h-25px bg-body shadow'
                      data-bs-toggle='tooltip'
                      title={intl.formatMessage(M.capturePhotoTooltip)}
                      onClick={() => {
                        setShowCameraModal(true)
                        setTimeout(() => startCamera(), 100)
                      }}
                    >
                      <i className='bi bi-camera-fill fs-7'></i>
                    </button>
                  </div>
                  {(avatarPreview || node.avatar) && (
                    <span
                      className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                      data-kt-image-input-action='remove'
                      data-bs-toggle='tooltip'
                      title={intl.formatMessage(M.removeAvatar)}
                      onClick={() => {
                        formik.setFieldValue('avatar', null)
                        setAvatarPreview(null)
                      }}
                    >
                      <i className='bi bi-x fs-2'></i>
                    </span>
                  )}
                </div>
                <div className='form-text'>
                  <FormattedMessage {...M.allowedFileTypes} />
                </div>
              </div>
            </div>

            <Row className='g-2'>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage id='admin.users.field.firstName' defaultMessage='First name' /> <small className='text-danger'>
                      <FormattedMessage id='validation.required' defaultMessage='(required)' />
                    </small>
                  </Form.Label>
                  <Form.Control
                    placeholder='First name'
                    name='firstName'
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(formik.errors.firstName && formik.touched.firstName)}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.firstName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage id='admin.users.field.lastName' defaultMessage='Last name' /> <small className='text-danger'>
                      <FormattedMessage id='validation.required' defaultMessage='(required)' />
                    </small>
                  </Form.Label>
                  <Form.Control
                    placeholder='Last name'
                    name='lastName'
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(formik.errors.lastName && formik.touched.lastName)}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.lastName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage id='admin.users.field.emailAddress' defaultMessage='Email address' /> <small className='text-danger'>
                      <FormattedMessage id='validation.required' defaultMessage='(required)' />
                    </small>
                  </Form.Label>
                  <Form.Control
                    placeholder='Email address'
                    type='email'
                    name='email'
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(formik.errors.email && formik.touched.email)}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage
                      id='admin.users.field.cellphoneNumber'
                      defaultMessage='Cellphone number'
                    />
                  </Form.Label>
                  <Form.Control
                    placeholder='Cellphone'
                    type='tel'
                    name='cellphone'
                    value={formik.values.cellphone}
                    onChange={(e) => {
                      let v = e.target.value
                      // Permite "+" solo al inicio y dígitos; elimina otros caracteres
                      v = v.replace(/(?!^\+)[^\d]/g, '')        // quita todo lo que no sea dígito excepto un '+' inicial
                      v = v.replace(/(?!^)\+/g, '')             // quita '+' extra si los hay
                      formik.setFieldValue('cellphone', v)
                    }}
                    maxLength={15}
                    isInvalid={Boolean(formik.errors.cellphone && formik.touched.cellphone)}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.cellphone}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage {...M.npn} />
                  </Form.Label>
                  <Form.Control
                    placeholder='NPN'
                    name='npn'
                    value={formik.values.npn}
                    onChange={formik.handleChange}

                  />

                </Form.Group>
                {/* <Form.Group>
                  <Form.Label>
                    ¿Apply deductions? <small className='text-danger'>(required)</small>
                  </Form.Label>
                  <Form.Select
                    name='applyDeductions'
                    value={formik.values.applyDeductions}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(
                      formik.errors.applyDeductions && formik.touched.applyDeductions
                    )}
                  >
                    <option value=''>Select One</option>
                    {['yes', 'no'].map((text) => (
                      <option key={text} value={text}>
                        {text}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.applyDeductions}
                  </Form.Control.Feedback>
                </Form.Group>*/}
                {/* <small>
                <span className='text-primary' style={{textDecoration: 'underline'}}>
                  Click here
                </span>{' '}
                to learn more about our access roles.
              </small> */}
              </Col>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage id='admin.users.field.role' defaultMessage='Rol' /> <small className='text-danger'>
                      <FormattedMessage id='validation.required' defaultMessage='(required)' />
                    </small>
                  </Form.Label>
                  <Form.Select
                    name='rol'
                    value={formik.values.rol}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(formik.errors.rol && formik.touched.rol)}
                    disabled={loadingRoles || isRoleSelectorDisabled}
                  >
                    {loading ? (
                      <option value=''>{intl.formatMessage(CM_LOADING)}</option>
                    ) : (
                      <option value=''>{intl.formatMessage(SELECT_ONE)}</option>
                    )}
                    {dataRoles?.allGroups.edges
                      .filter(({ node }) => {
                        // Si no es SuperAdministrador, no puede asignar rol SuperAdministrador
                        if (!isSuperAdministrador && node.name === 'SuperAdministrador') {
                          return false
                        }
                        // Si es Supervisor, no puede crear o asignar rol de Administrador
                        if (isSupervisor && node.name === 'Administrador') {
                          return false
                        }
                        return true
                      })
                      .map(({ node }) => (
                        <option key={node.id} value={node.id}>
                          {node.name}
                        </option>
                      ))}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>{formik.errors.rol}</Form.Control.Feedback>
                  {isRoleSelectorDisabled && (
                    <Form.Text className='text-muted'>
                      <FormattedMessage {...ADMIN_NO_OWN_ROLE} />
                    </Form.Text>
                  )}
                </Form.Group>
                {/* <small>
                <span className='text-primary' style={{textDecoration: 'underline'}}>
                  Click here
                </span>{' '}
                to learn more about our access roles.
              </small> */}
              </Col>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>
                    <FormattedMessage id='common.field.organization' defaultMessage='Organization' />
                  </Form.Label>
                  <Form.Select
                    name='organizationId'
                    value={formik.values.organizationId}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(
                      formik.errors.organizationId && formik.touched.organizationId
                    )}
                    disabled={loadingOrganizations || isOrganizationSelectorDisabled}
                  >
                    {loadingOrganizations ? (
                      <option value=''>{intl.formatMessage(CM_LOADING)}</option>
                    ) : (
                      <option value=''>{intl.formatMessage(SELECT_ONE)}</option>
                    )}
                    {dataOrganizations?.allOrganizations?.edges?.map(({ node }: any) => {
                      const value = normalizeId(node.id)
                      return (
                        <option key={node.id} value={value}>
                          {node.name}
                        </option>
                      )
                    })}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.organizationId}
                  </Form.Control.Feedback>
                  {isOrganizationSelectorDisabled && (
                    <Form.Text className='text-muted'>
                      <FormattedMessage {...ADMIN_ONLY_SUPERADMIN_ORG} />
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              {/* <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  Preferred language <small className='text-danger'>(required)</small>
                </Form.Label>
                <Form.Select>
                  <option>Spanish</option>
                  <option>English</option>
                </Form.Select>
              </Form.Group>
            </Col> */}
              {/* <Col className='mt-5'>
                <Form.Check
                  type='checkbox'
                  label={
                    <span className='text-dark'>
                      Check this box to send this user an email invitation.
                    </span>
                  }
                  defaultChecked
                />
              </Col> */}
            </Row>
          </div>

          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={loading || loadingUpload}
            >
              {!(loading || loadingUpload) && (
                <FormattedMessage id='common.action.saveChanges' defaultMessage='Save changes' />
              )}
              {(loading || loadingUpload) && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  <FormattedMessage {...M.pleaseWait} />
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Camera Modal */}
      <Modal show={showCameraModal} onHide={() => {
        stopCamera()
        setShowCameraModal(false)
      }} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title className='d-flex align-items-center gap-2'>
            <i className='bi bi-camera-fill text-success fs-5'></i>
            <FormattedMessage {...M.capturePhoto} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='d-flex flex-column gap-3'>
            <div style={{
              width: '100%',
              aspectRatio: '4/3',
              backgroundColor: '#000',
              borderRadius: '8px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </div>
            <div className='d-flex justify-content-center gap-2'>
              <Button
                variant='success'
                onClick={capturePhoto}
                className='d-flex align-items-center gap-2'
                disabled={!cameraActive}
              >
                <i className='bi bi-camera-fill'></i>
                <FormattedMessage {...M.capturePhoto} />
              </Button>
              <Button
                variant='outline-secondary'
                onClick={() => {
                  stopCamera()
                  setShowCameraModal(false)
                }}
              >
                <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Hidden canvas for capturing photos */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  )
}
